// src/render/audio/stinger-registry.ts
// Frozen stinger list — AUDIO-05 + 06-CONTEXT.md D-23. The full set of one-shot
// (and one looping) audio cues P6 fires from the renderBus is enumerated here
// in code; admin authoring CANNOT extend this list at runtime (V2-ADMIN-01 in
// spirit). Adding a new stinger is a code change + asset commit.
//
// Each entry maps a `StingerName` to a `manifestKey` (the lookup key in
// public/assets/audio/audio-manifest.json) plus optional Howler hints (`pool`
// for one-shot eviction, `loop` for the motion-light hum which is the only
// non-one-shot in the registry).
//
// Source: AUDIO-05 (REQUIREMENTS.md), 06-CONTEXT.md D-23, 06-PATTERNS.md
// stinger-registry.ts pattern assignment.

export type StingerName =
  | 'torch-on'
  | 'torch-off'
  | 'catch'
  | 'base-touch'
  | 'free-all'
  | 'motion-light-hum'
  | 'fear-gasp-afraid'
  | 'fear-gasp-terrified';

export interface StingerSpec {
  /** Key into public/assets/audio/audio-manifest.json. */
  manifestKey: string;
  /**
   * Optional Howler `pool` override for this stinger (default uses
   * P6_HOWL_POOL_STINGER from constants.ts). Useful if a particular
   * stinger needs a smaller / larger eviction pool.
   */
  pool?: number;
  /** True for the motion-light hum which loops while the light is armed. */
  loop?: boolean;
}

const STINGERS_RAW: Record<StingerName, StingerSpec> = {
  // D-23: torch on/off — two stingers (warmer + chillier per CONTEXT).
  'torch-on': { manifestKey: 'stingers/torch-on' },
  'torch-off': { manifestKey: 'stingers/torch-off' },
  // D-23: gameplay-core stingers.
  catch: { manifestKey: 'stingers/catch' },
  'base-touch': { manifestKey: 'stingers/base-touch' },
  'free-all': { manifestKey: 'stingers/free-all' },
  // D-23: motion-light hum — only LOOPING entry. Fades in over ~80ms when the
  // light arms; out over ~400ms when it fades (AUDIO-05 + VIS-05 alignment).
  'motion-light-hum': { manifestKey: 'stingers/motion-light-hum', loop: true },
  // D-14: one-shot fear gasps fired on `fear-threshold-crossed` upward at
  // afraid + terrified. No stinger on `nervous` (too small a jolt).
  'fear-gasp-afraid': { manifestKey: 'stingers/fear-gasp-afraid' },
  'fear-gasp-terrified': { manifestKey: 'stingers/fear-gasp-terrified' },
};

/**
 * Frozen stinger registry. `Object.freeze` enforces V2-ADMIN-01 spirit at
 * runtime — admin authoring cannot mutate the list, even via reflection.
 */
export const STINGERS: Readonly<Record<StingerName, Readonly<StingerSpec>>> =
  Object.freeze(STINGERS_RAW) as Readonly<Record<StingerName, Readonly<StingerSpec>>>;

/** Convenience: every StingerName as an array (for iteration / preload). */
export const STINGER_NAMES: readonly StingerName[] = Object.freeze(
  Object.keys(STINGERS_RAW) as StingerName[],
);
